import React, { useEffect } from 'react';
import styled from 'styled-components';
import { shallowEqual } from 'react-redux';
import { useIntl } from 'react-intl';
import jamespot, {
    jFileLittle,
    WidgetArticleAttachmentType,
    WidgetDisplayMode,
    WidgetArticleAttachmentContent,
    resolveFilePath,
} from 'jamespot-user-api';
import { Application, Toast, Widget, updateWidgetContent } from 'jamespot-front-business';
import { JRCFileViewerOpenWithOptionsType } from 'jamespot-react-components';
import { WidgetUploader } from '../components/WidgetUploader';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

const WidgetAttachment = commonComponentsRegistry.getLazyComponent('WidgetAttachment');
const JRCWidgetEmptyInplace = commonComponentsRegistry.getLazyComponent('WidgetEmptyInplace');
const FileViewer = commonComponentsRegistry.getLazyComponent('FileViewer');

// DUPLICATE FROM WEDOC
export const wedocWindowOpen = (idFile: number, hash: string, target?: '_blank') => {
    window.open(getWedocFilePath(idFile, hash), target);
};

// DUPLICATE FROM WEDOC
export function getWedocFilePath(idFile: number, hash: string) {
    return `/ng/rr/file/${idFile}/${hash}`;
}

export const CSSWrapper = styled.div`
    flex: 1;
`;

export const JRCWidgetArticleAttachmentWrapper = ({
    widget,
    inplace,
    mode,
}: {
    widget: WidgetArticleAttachmentType;
    inplace?: boolean;
    mode?: WidgetDisplayMode;
}) => {
    const intl = useIntl();
    const { uniqid } = widget;
    const { files } = widget.content;
    const dispatch = useAppDispatch();
    const isActive = useAppSelector((state) => Widget.selectors.isActive(state, uniqid), shallowEqual);

    const onError = (message?: string) => {
        dispatch(Toast.actions.error({ label: message ?? intl.formatMessage({ id: 'GLOBAL_Error' }) }));
    };

    useEffect(() => {
        dispatch(Widget.slice.actions.setWidgetState({ uniqid, mounted: true }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(
            Widget.slice.actions.setWidgetState({
                uniqid,
                empty: !files || files.length === 0,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    const weDoc = useAppSelector((state) => Application.selectors.selectById(state, 'WeDocHook'));

    const onClick = (file: jFileLittle | undefined) => {
        if (!file) {
            return;
        }
        jamespot.object
            .getAccessHash(`fileArticle/${file.id}`)
            .then((response) => {
                if (response.result === '') {
                    throw new Error('result empty');
                }
                const hash = response.result;
                wedocWindowOpen(file.id, hash);
            })
            .catch(() => {
                dispatch(Toast.actions.error({ label: 'WIDGET_Open_Failed' }, 6000));
            });
    };

    const option = [
        {
            label: intl.formatMessage({ id: 'WIDGET_Open_With_Wedoc' }),
            onClick: onClick,
        },
    ];

    const openWithOptions: JRCFileViewerOpenWithOptionsType = weDoc
        ? [
              {
                  condition: (f) =>
                      [
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                      ].includes(f.mimetype),
                  options: option,
              },
          ]
        : [];

    return files !== undefined ? (
        <CSSWrapper>
            {mode !== 'view' ? (
                <WidgetAttachment files={files} onClick={() => undefined} onError={onError} />
            ) : (
                <FileViewer files={files} openWithOptions={openWithOptions}>
                    {(open) => {
                        return (
                            <WidgetAttachment
                                files={files.map((f) => ({
                                    ...f,
                                    uri: resolveFilePath(f),
                                }))}
                                onClick={(idFile) => open(idFile)}
                                onError={onError}
                            />
                        );
                    }}
                </FileViewer>
            )}
            {inplace && mode !== 'view' && (
                <WidgetUploader
                    initialFiles={files}
                    initialClick={isActive}
                    multiple={true}
                    accept={'*'}
                    onUploadSuccess={(files) => {
                        if (Array.isArray(files)) {
                            dispatch(
                                updateWidgetContent<WidgetArticleAttachmentContent>(uniqid, {
                                    files: files.map((f) => ({
                                        ...f,
                                        uri: resolveFilePath(f) ?? '',
                                    })),
                                }),
                            );
                        }
                    }}
                    onUploadStart={() => {
                        dispatch(Widget.slice.actions.setWidgetState({ uniqid, loading: true }));
                    }}
                    onUploadEnd={() => {
                        dispatch(Widget.slice.actions.setWidgetState({ uniqid, loading: false }));
                    }}>
                    {mode === 'edit' ? (
                        <JRCWidgetEmptyInplace
                            label={'WIDGET_Article_Attachment_Empty_Label'}
                            description={'WIDGET_Article_Attachment_Empty_Description'}
                            url={'widget-article-image-placeholder.png'}
                            onClick={() => undefined}
                        />
                    ) : (
                        <></>
                    )}
                </WidgetUploader>
            )}
        </CSSWrapper>
    ) : (
        <></>
    );
};
